import React, { useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { ActivityIndicator, Text, useTheme } from 'react-native-paper';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  initializeAuth,
  selectIsAuthInitialized,
  selectIsAuthenticated,
} from '../../store/slices/authSlice';
import {
  fetchUserPreferences,
  selectUserPreferences,
} from '../../store/slices/userSlice';
import { useAuthContext } from '@coffeeplexity/shared';
import { useAutoLogout } from '../../hooks/useAutoLogout';
import { logger } from '../../utils/logger';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * AuthGuard component that:
 * 1. Initializes authentication state on app start
 * 2. Fetches user preferences when authenticated
 * 3. Automatically logs out users when they become unauthenticated
 * 4. Shows loading state during authentication initialization
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // Redux auth state
  const isReduxInitialized = useAppSelector(selectIsAuthInitialized);
  const isReduxAuthenticated = useAppSelector(selectIsAuthenticated);
  const userPreferences = useAppSelector(selectUserPreferences);

  // Shared auth context
  const { isAuthenticated: contextAuthenticated, isLoading: contextLoading } =
    useAuthContext();

  // Auto-logout hook
  useAutoLogout();

  // Initialize Redux auth state on mount
  useEffect(() => {
    if (!isReduxInitialized) {
      dispatch(initializeAuth());
    }
  }, [dispatch, isReduxInitialized]);

  // Fetch user preferences when authenticated
  useEffect(() => {
    if (isReduxAuthenticated && !userPreferences) {
      dispatch(fetchUserPreferences());
    }
  }, [dispatch, isReduxAuthenticated, userPreferences]);

  // Log authentication state for debugging
  useEffect(() => {
    logger.log('🔐 Auth State:', {
      redux: {
        initialized: isReduxInitialized,
        authenticated: isReduxAuthenticated,
      },
      context: { authenticated: contextAuthenticated, loading: contextLoading },
    });
  }, [
    isReduxInitialized,
    isReduxAuthenticated,
    contextAuthenticated,
    contextLoading,
  ]);

  // Show loading screen while authentication is being initialized
  if (!isReduxInitialized || contextLoading) {
    return (
      <View
        style={[
          styles.loadingContainer,
          { backgroundColor: theme.colors.background },
        ]}
      >
        <ActivityIndicator size='large' color={theme.colors.primary} />
        <Text
          variant='bodyLarge'
          style={[styles.loadingText, { color: theme.colors.onBackground }]}
        >
          Initializing...
        </Text>
      </View>
    );
  }

  return <>{children}</>;
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    textAlign: 'center',
  },
});
